import { useState, useRef, useEffect } from 'react';
import './Autorizacion.css';
import { evaluarAutorizacion } from '../../services/AutorizacionService';
import { buscarPracticas } from '../../services/NomencladorService';
import { obtenerAsociadoPorId, obtenerPlanVigentePorAsociado } from '../../services/AsociadoService';
import { obtenerReglaPorId } from '../../services/ReglasService';

// ─── Datos maestros ───────────────────────────────────────────────────────────
const DIAGNOSTICOS = [
  { id: 'D001', codigo: 'Z00.0', nombre: 'Control médico de rutina' },
  { id: 'D002', codigo: 'E11.9', nombre: 'Diabetes tipo 2 sin complicaciones' },
  { id: 'D003', codigo: 'I10',   nombre: 'Hipertensión arterial esencial' },
  { id: 'D004', codigo: 'E78.5', nombre: 'Hiperlipidemia mixta' },
  { id: 'D005', codigo: 'E03.9', nombre: 'Hipotiroidismo no especificado' },
  { id: 'D006', codigo: 'N39.0', nombre: 'Infección urinaria' },
  { id: 'D007', codigo: 'D50.9', nombre: 'Anemia ferropénica' },
  { id: 'D008', codigo: 'K76.0', nombre: 'Hígado graso no alcohólico' },
  { id: 'D009', codigo: 'J06.9', nombre: 'Infección respiratoria aguda' },
  { id: 'D010', codigo: 'R50.9', nombre: 'Fiebre de origen desconocido' },
];

const PLANES = [
  { id: 'BRO250', nombre: 'Bronce (BRO250)' },
  { id: 'AMN', nombre: 'AMN (AMN)' },
];

const TIPOS_PRACTICA = [
  { id: 'medica',       label: 'Práctica Médica', icon: '🩺', habilitado: true },
  { id: 'medicamento',  label: 'Medicamento',      icon: '💊', habilitado: false },
  { id: 'laboratorio',  label: 'Laboratorio',      icon: '🔬', habilitado: false  },
];

// ─── Pasos del flujograma ─────────────────────────────────────────────────────
const PASOS_FLUJO = [
  { id: 'canal',        label: 'Canal',          icon: '📱' },
  { id: 'orquestador', label: 'Orquestador',     icon: '⚙️'  },
  { id: 'nomenclador', label: 'Nomenclador',     icon: '📋' },
  { id: 'datos',       label: 'Datos Maestros',  icon: '🗃️'  },
  { id: 'motor',       label: 'Motor de Reglas', icon: '🧠' },
  { id: 'som',         label: 'SOM',             icon: '🖥️'  },
];

// ─── Normalizar decisión ──────────────────────────────────────────────────────
const canonicalizeDecision = (decision) =>
  String(decision ?? '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const normalizeDecision = (decision) => {
  const d = canonicalizeDecision(decision);
  const aliases = {
    AUTORIZADO: 'AUTORIZA',
    RECHAZADO: 'NO_AUTORIZA',
    DERIVADO: 'DERIVAR',
    DERIVA: 'DERIVAR',
    NO_REQUIERE_INFO: 'NO_REQUIERE_INFORMACION',
    NO_REQUIERE_INFORMACION_ADICIONAL: 'NO_REQUIERE_INFORMACION',
    TRAMITE_DUPLICADO: 'TRAMITE_DUPLICADO',
  };
  return aliases[d] || d;
};

const normalizeParamKey = (key) =>
  String(key || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');

const getParamValue = (parametros, acceptedKeys) => {
  if (!parametros || typeof parametros !== 'object') return '';
  const accepted = new Set((acceptedKeys || []).map((k) => normalizeParamKey(k)));
  for (const [key, value] of Object.entries(parametros)) {
    if (!accepted.has(normalizeParamKey(key))) continue;
    if (value === null || value === undefined) return '';
    return String(value);
  }
  return '';
};

const extractRuleIdFromDisparada = (ruleName) => {
  const raw = String(ruleName || '').trim();
  const match = raw.match(/^Regla_(.+)_V\d+$/);
  return match ? match[1] : '';
};

const firstNonBlank = (...values) => {
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (text) return text;
  }
  return '';
};

const latin1ToUtf8 = (text) => {
  try {
    const bytes = Array.from(text || '')
      .map((ch) => `%${ch.charCodeAt(0).toString(16).padStart(2, '0')}`)
      .join('');
    return decodeURIComponent(bytes);
  } catch {
    return text;
  }
};

const utf8ToLatin1 = (text) => {
  try {
    const bytes = new TextEncoder().encode(text);
    return Array.from(bytes).map((b) => String.fromCharCode(b)).join('');
  } catch {
    return text;
  }
};

const textQualityScore = (text) => {
  const s = String(text || '');
  const suspiciousPairs =
    (s.match(/Ã./g) || []).length +
    (s.match(/Â./g) || []).length +
    (s.match(/â./g) || []).length +
    (s.match(/Ð./g) || []).length +
    (s.match(/Ñ./g) || []).length;
  const replacementChar = (s.match(/�/g) || []).length;
  const brokenQuestion = (s.match(/[A-Za-zÁÉÍÓÚáéíóúÑñ]\?[A-Za-zÁÉÍÓÚáéíóúÑñ]/g) || []).length;
  const tooManyControls = Array.from(s).filter((ch) => {
    const code = ch.charCodeAt(0);
    return code < 32 && code !== 9 && code !== 10 && code !== 13;
  }).length;
  const goodAccents = (s.match(/[áéíóúÁÉÍÓÚñÑüÜ]/g) || []).length;

  return (goodAccents * 2) - (suspiciousPairs * 5) - (replacementChar * 6) - (brokenQuestion * 4) - (tooManyControls * 8);
};

const normalizeApiText = (value) => {
  if (typeof value !== 'string') return value;
  const raw = value.trim();
  if (!raw) return raw;

  const candidates = [
    raw,
    latin1ToUtf8(raw),
    latin1ToUtf8(latin1ToUtf8(raw)),
    utf8ToLatin1(raw),
    latin1ToUtf8(utf8ToLatin1(raw)),
  ];

  let best = raw;
  let bestScore = textQualityScore(raw);

  candidates.forEach((candidate) => {
    const cleaned = String(candidate || '')
      .replace(/[“”]/g, '"')
      .replace(/[’]/g, "'")
      .replace(/\s{2,}/g, ' ')
      .trim();
    const score = textQualityScore(cleaned);
    if (score > bestScore) {
      best = cleaned;
      bestScore = score;
    }
  });

  return best;
};

const normalizeApiPayload = (payload) => {
  if (Array.isArray(payload)) return payload.map(normalizeApiPayload);
  if (payload && typeof payload === 'object') {
    return Object.fromEntries(
      Object.entries(payload).map(([k, v]) => [k, normalizeApiPayload(v)])
    );
  }
  return normalizeApiText(payload);
};

const formatMotivoDisplay = (motivo) => {
  const text = String(motivo ?? '').trim();
  if (!text) return '';
  return text
    .replace(/([.!?])\s+/g, '$1\n')
    .replace(/\n([a-záéíóúñ])/g, (_, c) => `\n${c.toUpperCase()}`);
};

const splitMotivoItems = (motivo) =>
  formatMotivoDisplay(motivo)
    .split('\n')
    .map((part) => part.trim())
    .filter(Boolean);

const normalizeComparableText = (value) =>
  String(value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .replace(/\s+/g, ' ');

const decisionVisualConfig = (decisionRaw) => {
  const decision = normalizeDecision(decisionRaw);
  if (decision === 'AUTORIZA') {
    return { key: 'autoriza', icon: '✓', headline: 'Autorización aprobada' };
  }
  if (decision === 'NO_AUTORIZA') {
    return { key: 'no_autoriza', icon: '✕', headline: 'Solicitud rechazada' };
  }
  if (decision === 'DERIVAR') {
    return { key: 'derivar', icon: '↗', headline: 'Caso derivado a revisión' };
  }
  if (decision === 'NO_REQUIERE_AUTORIZACION') {
    return { key: 'no_requiere_autorizacion', icon: 'i', headline: 'No requiere autorización' };
  }
  if (decision === 'NO_REQUIERE_INFORMACION') {
    return { key: 'no_requiere_informacion', icon: 'i', headline: 'No requiere información adicional' };
  }
  if (decision === 'TRAMITE_DUPLICADO') {
    return { key: 'duplicado', icon: '!', headline: 'Trámite duplicado' };
  }
  if (decision === 'ERROR') {
    return { key: 'error', icon: '!', headline: 'No se pudo evaluar la solicitud' };
  }
  return { key: 'neutro', icon: '•', headline: 'Resultado de evaluación' };
};

// ─── SearchSelect ─────────────────────────────────────────────────────────────
function SearchSelect({
  options,
  value,
  onChange,
  placeholder,
  valueKey = 'id',
  labelKey = 'nombre',
  renderLabel,
  onSearch,
  isLoading = false,
  emptyLabel = 'Sin resultados',
  loadingLabel = 'Buscando...'
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen]   = useState(false);
  const ref               = useRef(null);

  const selected = options.find(o => o[valueKey] === value);
  const filtered = options.filter(o => {
    const text = renderLabel ? renderLabel(o) : o[labelKey];
    return text.toLowerCase().includes(query.toLowerCase());
  });

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (opt) => { onChange(opt[valueKey]); setQuery(''); setOpen(false); };
  const displayValue = selected ? (renderLabel ? renderLabel(selected) : selected[labelKey]) : null;

  return (
    <div className="ss-wrapper" ref={ref}>
      <div className={`ss-control${open ? ' ss-control--open' : ''}`} onClick={() => setOpen(o => !o)}>
        <span className={`ss-value${!displayValue ? ' ss-value--placeholder' : ''}`}>
          {displayValue || placeholder}
        </span>
        <span className="ss-arrow">{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div className="ss-dropdown">
          <div className="ss-search-wrap">
            <input
              autoFocus
              className="ss-search"
              placeholder="Buscar..."
              value={query}
              onChange={e => {
                const next = e.target.value;
                setQuery(next);
                if (onSearch) onSearch(next);
              }}
              onClick={e => e.stopPropagation()}
            />
          </div>
          <div className="ss-options">
            {isLoading
              ? <div className="ss-no-results">{loadingLabel}</div>
              : filtered.length === 0
                ? <div className="ss-no-results">{emptyLabel}</div>
                : filtered.map(opt => (
                  <div
                    key={opt[valueKey]}
                    className={`ss-option${opt[valueKey] === value ? ' ss-option--selected' : ''}`}
                    onClick={() => handleSelect(opt)}
                  >
                    {renderLabel ? renderLabel(opt) : opt[labelKey]}
                  </div>
                ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Flujograma ───────────────────────────────────────────────────────────────
function SimulacionFlujo({ logs, pasoActual, activeLogIndex }) {
  const logRef = useRef(null);
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logs]);

  const getStatus = (i) => {
    if (pasoActual === null) return 'done';
    if (i < pasoActual)     return 'done';
    if (i === pasoActual)   return 'active';
    return 'pending';
  };

  return (
    <div className="sim-wrapper">
      <div className="sim-header">
        <span className="sim-header-label">▶ Flujo de autorización en tiempo real</span>
      </div>
      <div className="sim-nodes">
        {PASOS_FLUJO.map((paso, i) => {
          const status = getStatus(i);
          const isLast = i === PASOS_FLUJO.length - 1;
          return (
            <div key={paso.id} className="sim-node-group">
              <div className={`sim-node sim-node--${status}`}>
                <span className={`sim-node-icon${status === 'active' ? ' sim-node-icon--pulse' : ''}`}>{paso.icon}</span>
                <span className="sim-node-label">{paso.label}</span>
                <span className={`sim-node-dot sim-node-dot--${status}`} />
              </div>
              {!isLast && <span className={`sim-connector sim-connector--${status === 'done' ? 'done' : 'pending'}`} />}
            </div>
          );
        })}
      </div>
      <div className="sim-log" ref={logRef}>
        <div className="sim-log-title">Log de transacciones</div>
        {logs.length === 0
          ? <span className="sim-log-empty">Esperando inicio...</span>
          : logs.map((line, i) => (
              <div key={i} className={`sim-log-line${i === activeLogIndex ? ' sim-log-line--active' : ''}`}>
                › {line}
              </div>
            ))
        }
      </div>
    </div>
  );
}

// ─── Step Indicator ───────────────────────────────────────────────────────────
function StepIndicator({ step, total }) {
  const labels = ['Tipo', 'Asociado', 'Práctica', 'Resumen'];
  return (
    <div className="step-indicator">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className="step-indicator__item">
          <div className={`step-indicator__dot${i + 1 === step ? ' step-indicator__dot--active' : i + 1 < step ? ' step-indicator__dot--done' : ''}`}>
            {i + 1 < step ? '✓' : i + 1}
          </div>
          {i < total - 1 && (
            <div className={`step-indicator__line${i + 1 < step ? ' step-indicator__line--done' : ''}`} />
          )}
        </div>
      ))}
      <span className="step-indicator__label">{labels[step - 1]}</span>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
function Autorizacion() {
  const TOTAL_STEPS = 4;
  const [step, setStep]             = useState(1);
  const [tipoPractica, setTipoPractica] = useState(null);

  // Campos precargados
  const [nroAfiliado, setNroAfiliado] = useState('3452666');
  const [afiliadoValidationState, setAfiliadoValidationState] = useState('idle');
  const [afiliadoValidationMessage, setAfiliadoValidationMessage] = useState('');
  const [planValidationState, setPlanValidationState] = useState('idle');
  const [planValidationMessage, setPlanValidationMessage] = useState('');
  const [plan, setPlan]               = useState('');
  const [practica, setPractica]       = useState('');
  const [diagnostico, setDiagnostico] = useState('D001');
  const [fechaOrden, setFechaOrden]   = useState(new Date().toISOString().split('T')[0]);
  const [fileOrden, setFileOrden]     = useState(null);
  const [prestadorId, setPrestadorId] = useState('11111111-1111-1111-1111-111111111111');
  const [practicas, setPracticas]     = useState([]);
  const [practicasLoading, setPracticasLoading] = useState(false);
  const [practicaQuery, setPracticaQuery] = useState('');

  // Simulación
  const [mensaje, setMensaje]               = useState(null);
  const [loading, setLoading]               = useState(false);
  const [showSim, setShowSim]               = useState(false);
  const [simLogs, setSimLogs]               = useState([]);
  const [simPaso, setSimPaso]               = useState(null);
  const [simDone, setSimDone]               = useState(false);
  const [activeLogIndex, setActiveLogIndex] = useState(null);
  const [copiedParamKey, setCopiedParamKey] = useState('');
  const [descripcionReglaResultado, setDescripcionReglaResultado] = useState('');
  const afiliadoValidationSeqRef            = useRef(0);
  const resultPanelRef                      = useRef(null);

  const addLog = (msg) => setSimLogs(prev => {
    const next = [...prev, msg];
    setActiveLogIndex(next.length - 1);
    return next;
  });
  const delay = (ms) => new Promise(res => setTimeout(res, ms));

  const practicaObj    = practicas.find(p => p.id === practica);
  const diagnosticoObj = DIAGNOSTICOS.find(d => d.id === diagnostico);
  const planObj        = PLANES.find(p => p.id === plan);
  const tipoObj        = TIPOS_PRACTICA.find(t => t.id === tipoPractica);
  const prestacionCodigo = practica;
  const puedeNuevaSolicitud = step === 4 && simDone && Boolean(mensaje) && !loading;
  const decisionVisual = decisionVisualConfig(mensaje?.decision);
  const decisionNormalizada = normalizeDecision(mensaje?.decision);
  const idTransaccionEngage = getParamValue(mensaje?.parametros, ['idTransaccionEngage']);
  const faseCorte = getParamValue(mensaje?.parametros, ['faseCorte']);
  const faseCorteVisual = String(faseCorte || '').trim().toUpperCase() === 'PRESTACION'
    ? 'COBERTURA'
    : faseCorte;
  const numeroSeguimientoSom = getParamValue(mensaje?.parametros, ['numeroSeguimientoSom']);
  const descripcionRegla = String(descripcionReglaResultado || '').trim();
  const motivosTextoNormalizado = (Array.isArray(mensaje?.motivos) ? mensaje.motivos : [])
    .flatMap((m) => splitMotivoItems(m))
    .map((m) => normalizeComparableText(m))
    .filter(Boolean)
    .join(' ');
  const descripcionNormalizada = normalizeComparableText(descripcionRegla);
  const descripcionRepetidaEnMotivos = Boolean(descripcionNormalizada) && (
    descripcionNormalizada === motivosTextoNormalizado ||
    motivosTextoNormalizado.includes(descripcionNormalizada) ||
    descripcionNormalizada.includes(motivosTextoNormalizado)
  );
  const ocultarDescripcionPorDerivacionElegibilidad =
    decisionNormalizada === 'DERIVAR' &&
    String(faseCorte || '').trim().toUpperCase() === 'ELEGIBILIDAD';
  const parameterBoxes = [
    { key: 'idTransaccionEngage', label: 'ID Transacción Engage', value: idTransaccionEngage, copyable: true, visible: Boolean(idTransaccionEngage) },
    { key: 'seguimientoSom', label: 'Seguimiento SOM', value: numeroSeguimientoSom, copyable: true, visible: Boolean(numeroSeguimientoSom) },
    { key: 'faseCorte', label: 'Fase de corte', value: faseCorteVisual, copyable: false, visible: Boolean(faseCorteVisual) },
    {
      key: 'descripcionRegla',
      label: 'Nombre de la regla',
      value: descripcionRegla,
      copyable: false,
      visible:
        Boolean(descripcionRegla) &&
        !ocultarDescripcionPorDerivacionElegibilidad &&
        !descripcionRepetidaEnMotivos,
    },
  ].filter((box) => box.visible);

  const handleSelectTipo = (tipo) => {
    if (!tipo?.habilitado) return;
    setTipoPractica(tipo.id);
    setPractica('');
    setPracticaQuery('');
    setStep(2);
  };

  const resetResultadoYSimulacion = () => {
    setMensaje(null);
    setShowSim(false);
    setSimLogs([]);
    setSimPaso(null);
    setSimDone(false);
    setActiveLogIndex(null);
    setCopiedParamKey('');
    setDescripcionReglaResultado('');
  };

  const handleCopyParam = async (key, value) => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopiedParamKey(key);
      setTimeout(() => setCopiedParamKey(''), 1200);
    } catch (e) {
    }
  };

  useEffect(() => {
    if (!mensaje || !simDone) return;
    const timer = setTimeout(() => {
      resultPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
    return () => clearTimeout(timer);
  }, [mensaje, simDone]);

  useEffect(() => {
    let active = true;

    const resolverDescripcion = async () => {
      setDescripcionReglaResultado('');
      const disparadas = Array.isArray(mensaje?.reglasDisparadas) ? mensaje.reglasDisparadas : [];
      if (!disparadas.length) return;

      for (let i = disparadas.length - 1; i >= 0; i -= 1) {
        const ruleName = String(disparadas[i] || '');
        const ruleId = extractRuleIdFromDisparada(ruleName);
        if (!ruleId) continue;

        try {
          const regla = await obtenerReglaPorId(ruleId);
          if (!active) return;
          const descripcion = firstNonBlank(regla?.nombre, regla?.descripcion);
          if (descripcion) {
            setDescripcionReglaResultado(descripcion);
            return;
          }
        } catch {
          // Si no existe esa regla en reglas_data (ej: sintética/interna), probamos la anterior.
        }
      }

      if (!active) return;
      setDescripcionReglaResultado('');
    };

    resolverDescripcion();
    return () => { active = false; };
  }, [mensaje?.reglasDisparadas]);

  const handleBackStep = () => {
    if (step <= 1) return;
    if (step === 4) {
      resetResultadoYSimulacion();
    }
    if (step === 2) {
      setStep(1);
      return;
    }
    setStep(s => s - 1);
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const query = practicaQuery.trim();
      if (query.length < 2) {
        setPracticas([]);
        setPracticasLoading(false);
        return;
      }

      setPracticasLoading(true);
      try {
        const numericQuery = /^[0-9.]+$/.test(query);
        const data = await buscarPracticas({
          codigo: numericQuery ? query : undefined,
          descripcion: numericQuery ? undefined : query,
          page: 0,
          size: 30,
        });
        if (cancelled) return;

        const items = Array.isArray(data?.content) ? data.content : [];
        const options = items
          .map((i) => ({
            id: String(i?.codigoItemCobertura ?? ""),
            nombre: i?.descripcion ?? "",
            fueraDePmo:
              i?.fueraDePmo === true || /^99[0-9]{5}$/.test(String(i?.codigoItemCobertura ?? "").trim()),
          }))
          .filter((opt) => opt.id && opt.nombre);

        setPracticas(options);
      } catch (e) {
        setPracticas([]);
      } finally {
        if (!cancelled) setPracticasLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [practicaQuery]);


  useEffect(() => {
    if (step !== 2) return;

    const afiliado = nroAfiliado.trim();
    if (!afiliado) {
      setAfiliadoValidationState('idle');
      setAfiliadoValidationMessage('');
      setPlanValidationState('idle');
      setPlanValidationMessage('');
      setPlan('');
      return;
    }

    const requestId = ++afiliadoValidationSeqRef.current;
    setAfiliadoValidationState('loading');
    setPlanValidationState('loading');
    setPlanValidationMessage('');
    setPlan('');

    const timer = setTimeout(async () => {
      try {
        const asociadoId = parseInt(afiliado, 10);
        await obtenerAsociadoPorId(asociadoId);
        if (requestId !== afiliadoValidationSeqRef.current) return;

        let planVigente = null;
        try {
          planVigente = await obtenerPlanVigentePorAsociado(asociadoId);
        } catch (errorPlan) {
          if (requestId !== afiliadoValidationSeqRef.current) return;
          const statusPlan = errorPlan?.response?.status;
          const apiPlanMessage = errorPlan?.response?.data?.message;

          if (statusPlan === 404) {
            setAfiliadoValidationState('invalid');
            setAfiliadoValidationMessage('El asociado no tiene un plan asignado.');
            setPlanValidationState('invalid');
            setPlanValidationMessage(apiPlanMessage || 'El asociado no tiene un plan asignado.');
            setPlan('');
            return;
          }

          const msgSistema = 'No se pudo buscar el plan por un error del sistema.';
          setAfiliadoValidationState('invalid');
          setAfiliadoValidationMessage(msgSistema);
          setPlanValidationState('invalid');
          setPlanValidationMessage(msgSistema);
          setPlan('');
          return;
        }

        if (!planVigente?.planCodigo) {
          setAfiliadoValidationState('invalid');
          setAfiliadoValidationMessage('El asociado no tiene un plan asignado.');
          setPlanValidationState('invalid');
          setPlanValidationMessage('El asociado no tiene un plan asignado.');
          setPlan('');
          return;
        }

        setAfiliadoValidationState('valid');
        setAfiliadoValidationMessage('');
        setPlan(String(planVigente.planCodigo).toUpperCase());
        setPlanValidationState('valid');
        setPlanValidationMessage('');
      } catch (error) {
        if (requestId !== afiliadoValidationSeqRef.current) return;
        const status = error?.response?.status;
        const apiMessage = error?.response?.data?.message;
        setAfiliadoValidationState('invalid');

        if (status === 404) {
          setAfiliadoValidationMessage(apiMessage || 'No existe el asociado.');
          setPlanValidationState('idle');
          setPlanValidationMessage('');
        } else {
          const msgSistema = 'No se pudo buscar el asociado por un error del sistema.';
          setAfiliadoValidationMessage(msgSistema);
          setPlanValidationState('invalid');
          setPlanValidationMessage(msgSistema);
        }

        setPlan('');
      }
    }, 450);

    return () => clearTimeout(timer);
  }, [nroAfiliado, step]);

  const canNext = () => {
    if (step === 2) return nroAfiliado && plan && prestadorId && afiliadoValidationState === 'valid' && planValidationState === 'valid';
    if (step === 3) return prestacionCodigo && diagnostico && fechaOrden;
    return true;
  };

  const handleAutorizar = async () => {
    setLoading(true);
    setMensaje(null);
    setShowSim(true);
    setSimLogs([]);
    setSimPaso(null);
    setSimDone(false);
    setActiveLogIndex(null);

    const solicitud = {
      afiliadoId:       parseInt(nroAfiliado),
      planCodigo:       plan,
      prestacionCodigo: String(prestacionCodigo),
      prestadorId:      prestadorId,
      fechaDeOrden:     fechaOrden,
    };

    try {
      setSimPaso(0);
      addLog(`[Canal] Iniciando solicitud — Tipo: ${tipoObj?.label}`);
      addLog(`[Canal] Asociado: ${nroAfiliado} | Práctica: ${practicaObj?.nombre}`);
      await delay(700);

      setSimPaso(1);
      addLog(`[Orquestador] Plan: ${planObj?.nombre}`);
      await delay(750);

      setSimPaso(2);
      addLog(`[Nomenclador] Validando "${practicaObj?.nombre || 'Práctica'}" (${prestacionCodigo})...`);
      await delay(800);
      addLog(`[Nomenclador] ✓ Práctica válida.`);
      await delay(300);

      setSimPaso(3);
      addLog(`[Datos] Buscando asociado ID: ${nroAfiliado}...`);
      await delay(800);
      addLog(`[Datos] ✓ Asociado encontrado. Plan: ${planObj?.nombre}.`);
      addLog(`[Datos] Diagnóstico: ${diagnosticoObj?.codigo} — ${diagnosticoObj?.nombre}`);
      await delay(300);

      setSimPaso(4);
      addLog(`[Motor] Evaluando reglas de autorización...`);
      await delay(500);
      addLog(`[Motor] Checkeando mora administrativa...`);
      await delay(400);
      addLog(`[Motor] Validando cobertura del plan...`);
      await delay(400);
      addLog(`[Motor] Checkeando topes de práctica...`);

      const respuesta    = await evaluarAutorizacion(solicitud);
      const respuestaNormalizada = normalizeApiPayload(respuesta);
      const decisionNorm = normalizeDecision(respuestaNormalizada.decision);
      addLog(`[Motor] → Decisión: ${respuesta.decision}`);
      await delay(400);

      setSimPaso(5);
      if (decisionNorm === 'AUTORIZA') {
        addLog(`[SOM] Generando autorización...`);
        await delay(600);
        addLog(`[SOM] ✓ Autorización registrada correctamente.`);
      } else if (decisionNorm === 'NO_AUTORIZA') {
        addLog(`[SOM] Registrando rechazo...`);
        await delay(500);
        addLog(`[SOM] ✗ Solicitud no autorizada.`);
      } else {
        addLog(`[SOM] Derivando a auditoría médica...`);
        await delay(600);
        addLog(`[SOM] ⚠ Caso derivado para revisión manual.`);
      }
      await delay(400);

      setSimPaso(null);
      setActiveLogIndex(null);

      const tipo =
        decisionNorm === 'AUTORIZA'    ? 'elegible'    :
        decisionNorm === 'NO_AUTORIZA' ? 'no-elegible' : 'neutro';

      setMensaje({
        decision: respuestaNormalizada.decision,
        motivos: respuestaNormalizada.motivos || [],
        tipo,
        reglasDisparadas: respuestaNormalizada.reglasDisparadas,
        parametros: respuestaNormalizada.parametros || null
      });

    } catch (error) {
      setSimPaso(null);
      setActiveLogIndex(null);
      addLog(`[Error] No se pudo conectar con el servicio.`);
      const errorData = error?.response?.data;
      if (errorData?.code === 'TRAMITE_DUPLICADO') {
        setMensaje({
          decision: 'TRÁMITE DUPLICADO',
          motivos: errorData?.message ? [normalizeApiText(errorData.message)] : [],
          tipo: 'duplicado'
        });
      } else {
        setMensaje({ decision: 'ERROR', motivos: ['No se pudo conectar con el servicio de autorización.'], tipo: 'neutro' });
      }
    } finally {
      setSimDone(true);
      setLoading(false);
    }
  };

  const handleLimpiar = () => {
    setStep(1);
    setTipoPractica(null);
    setNroAfiliado('3452666');
    setAfiliadoValidationState('idle');
    setAfiliadoValidationMessage('');
    setPlanValidationState('idle');
    setPlanValidationMessage('');
    setPlan('');
    setPractica('');
    setDiagnostico('D001');
    setFechaOrden(new Date().toISOString().split('T')[0]);
    setFileOrden(null);
    setPrestadorId('11111111-1111-1111-1111-111111111111');
    resetResultadoYSimulacion();
  };

  return (
    <div className="page-content">
      <div className="controls-section">
        <div className="auth-card">

          {/* Header */}
          <div className="auth-card__header">
            <div>
              <h2 className="auth-card__title">Solicitud de Autorización</h2>
              <p className="auth-card__subtitle">Prácticas ambulatorias</p>
            </div>
            {tipoPractica && <StepIndicator step={step} total={TOTAL_STEPS} />}
          </div>

          {/* Cartel resultado */}
          {mensaje && simDone && (
            <div className="auth-card__result" ref={resultPanelRef}>
              <div className={`result-panel result-panel--${mensaje.tipo} result-panel--decision-${decisionVisual.key}`}>
                <div className="result-panel__header">
                  <div className="result-panel__header-left">
                    <span className="result-panel__decision result-panel__decision--dynamic">
                      <span className="result-panel__decision-icon">{decisionVisual.icon}</span>
                      {mensaje.decision}
                    </span>
                    <span className="result-panel__subtitle">Resultado de evaluación</span>
                  </div>
                </div>
                <div className="result-panel__headline">{decisionVisual.headline}</div>

                {parameterBoxes.length > 0 && (
                  <div className="result-panel__meta">
                    {parameterBoxes.map((box) => (
                      <div key={box.key} className="result-chip">
                        <span className="result-chip__label">{box.label}</span>
                        <span className="result-chip__value">{String(box.value)}</span>
                        {box.copyable && (
                          <button
                            type="button"
                            className="result-chip__copy-btn"
                            onClick={() => handleCopyParam(box.key, box.value)}
                            title="Copiar valor"
                          >
                            {copiedParamKey === box.key ? 'Copiado' : 'Copiar'}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="result-panel__content-grid">
                  <div className="result-panel__section result-panel__section--card">
                    <span className="result-panel__section-title">Reglas disparadas</span>
                    <div className="result-rules">
                      {mensaje.reglasDisparadas?.length > 0 ? (
                        mensaje.reglasDisparadas.map((r, i) => (
                          <span key={i} className="result-rule-tag">{r}</span>
                        ))
                      ) : (
                        <span className="result-panel__empty">Sin reglas disparadas</span>
                      )}
                    </div>
                  </div>

                  <div className="result-panel__section result-panel__section--card">
                    <span className="result-panel__section-title">Motivos</span>
                    <div className="result-reasons">
                      {mensaje.motivos?.length > 0 ? (
                        mensaje.motivos.flatMap((m) => splitMotivoItems(m)).map((motivoItem, i) => (
                          <div key={i} className="result-reason-item">
                            <span className="result-reason-item__dot" />
                            <span>{motivoItem}</span>
                          </div>
                        ))
                      ) : (
                        <span className="result-panel__empty">Sin motivos informados</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="auth-card__body">

            {/* ── PASO 1: Tipo de práctica ──────────────────────────────── */}
            {step === 1 && (
              <div className="auth-step">
                <h3 className="auth-step__title">¿Qué tipo de práctica es?</h3>
                <div className="tipo-grid">
                  {TIPOS_PRACTICA.map(tipo => (
                    <div
                      key={tipo.id}
                      className={`tipo-card${tipoPractica === tipo.id ? ' tipo-card--selected' : ''}${!tipo.habilitado ? ' tipo-card--disabled' : ''}`}
                      onClick={() => handleSelectTipo(tipo)}
                      title={!tipo.habilitado ? 'Disponible próximamente' : undefined}
                    >
                      <span className="tipo-card__icon">{tipo.icon}</span>
                      <span className="tipo-card__label">{tipo.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── PASO 2: Datos del asociado ────────────────────────────── */}
            {step === 2 && (
              <div className="auth-step">
                <h3 className="auth-step__title">Datos del asociado</h3>
                <div className="auth-form-grid">
                  <div className="auth-field">
                    <label className="auth-field__label">Número de asociado</label>
                    <div className="auth-field__input-wrap">
                      <input
                        type="text"
                        value={nroAfiliado}
                        onChange={e => {
                          setNroAfiliado(e.target.value.replace(/[^0-9]/g, ''));
                          if (afiliadoValidationState !== 'idle') setAfiliadoValidationState('idle');
                          if (afiliadoValidationMessage) setAfiliadoValidationMessage('');
                          if (planValidationState !== 'idle') setPlanValidationState('idle');
                          if (planValidationMessage) setPlanValidationMessage('');
                          if (plan) setPlan('');
                        }}
                        placeholder="Ej: 123456"
                        className={`auth-field__input auth-field__input--with-indicator ${afiliadoValidationState === 'valid' ? 'auth-field__input--valid' : ''} ${afiliadoValidationState === 'invalid' ? 'auth-field__input--invalid' : ''}`}
                      />
                      {afiliadoValidationState === 'loading' && (
                        <span className="auth-field__status auth-field__status--loading" aria-hidden="true">
                          <span className="auth-field__spinner" />
                        </span>
                      )}
                      {afiliadoValidationState === 'valid' && (
                        <span className="auth-field__status auth-field__status--valid" aria-label="Asociado válido">✓</span>
                      )}
                      {afiliadoValidationState === 'invalid' && (
                        <span className="auth-field__status auth-field__status--invalid" aria-label="Asociado no encontrado">✕</span>
                      )}
                    </div>
                    {afiliadoValidationState === 'invalid' && (
                      <div className="auth-field__hint auth-field__hint--error">{afiliadoValidationMessage || 'No se encontró el asociado.'}</div>
                    )}
                    {afiliadoValidationState === 'valid' && (
                      <div className="auth-field__hint auth-field__hint--ok">Asociado válido.</div>
                    )}
                  </div>
                  <div className="auth-field">
                    <label className="auth-field__label">Plan</label>
                    <div className="auth-field__input-wrap">
                      <input
                        value={plan}
                        placeholder={planValidationState === 'loading' ? 'Buscando plan...' : 'Plan autocompletado'}
                        className={`auth-field__input auth-field__input--with-indicator ${planValidationState === 'valid' ? 'auth-field__input--valid' : ''} ${planValidationState === 'invalid' ? 'auth-field__input--invalid' : ''}`}
                        readOnly
                      />
                      {planValidationState === 'loading' && (
                        <span className="auth-field__status auth-field__status--loading" aria-hidden="true">
                          <span className="auth-field__spinner" />
                        </span>
                      )}
                      {planValidationState === 'valid' && (
                        <span className="auth-field__status auth-field__status--valid" aria-label="Plan válido">✓</span>
                      )}
                      {planValidationState === 'invalid' && (
                        <span className="auth-field__status auth-field__status--invalid" aria-label="Plan no encontrado">✕</span>
                      )}
                    </div>
                    {planValidationState === 'loading' && (
                      <div className="auth-field__hint">Buscando plan vigente...</div>
                    )}
                    {planValidationState === 'valid' && (
                      <div className="auth-field__hint auth-field__hint--ok">Plan vigente validado.</div>
                    )}
                    {planValidationState === 'invalid' && (
                      <div className="auth-field__hint auth-field__hint--error">{planValidationMessage || 'No se pudo validar plan vigente.'}</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── PASO 3: Práctica, diagnóstico y fecha ────────────────── */}
            {step === 3 && (
              <div className="auth-step">
                <h3 className="auth-step__title">
                  {tipoObj?.icon} {tipoObj?.label} — Práctica e indicación
                </h3>
                <div className="auth-form-grid auth-form-grid--2col">
                  <div className="auth-field">
                    <label className="auth-field__label">
                      {tipoPractica === 'medicamento' ? 'Medicamento' : tipoPractica === 'laboratorio' ? 'Práctica de laboratorio' : 'Práctica médica'}
                    </label>
                    <SearchSelect
                      options={practicas}
                      value={practica}
                      onChange={(value) => {
                        setPractica(value);
                      }}
                      placeholder="Buscar práctica..."
                      renderLabel={opt => `${opt.id} — ${opt.nombre}${opt.fueraDePmo ? " · fuera de PMO" : ""}`}
                      onSearch={setPracticaQuery}
                      isLoading={practicasLoading}
                      emptyLabel={practicaQuery.trim().length < 2 ? 'Escribi al menos 2 caracteres.' : 'Sin resultados'}
                      loadingLabel="Buscando en nomenclador..."
                    />
                    {practicaObj && (
                      <div className="auth-field__badge">{tipoObj?.icon} {practicaObj.id} — {practicaObj.nombre}</div>
                    )}
                    {practicasLoading && (
                      <div className="auth-field__badge">Cargando prácticas del nomenclador...</div>
                    )}
                  </div>
                  <div className="auth-field">
                    <label className="auth-field__label">Diagnóstico (CIE-10)</label>
                    <SearchSelect
                      options={DIAGNOSTICOS}
                      value={diagnostico}
                      onChange={setDiagnostico}
                      placeholder="Buscar diagnóstico..."
                      renderLabel={opt => `${opt.codigo} — ${opt.nombre}`}
                    />
                    {diagnosticoObj && (
                      <div className="auth-field__badge">🏥 {diagnosticoObj.codigo} — {diagnosticoObj.nombre}</div>
                    )}
                  </div>
                  <div className="auth-field">
                    <label className="auth-field__label">Fecha de orden médica</label>
                    <input
                      type="date"
                      value={fechaOrden}
                      onChange={e => setFechaOrden(e.target.value)}
                      className="auth-field__input"
                    />
                  </div>
                  <div className="auth-field">
                    <label className="auth-field__label">Adjuntar orden médica</label>
                    <div
                      className={`auth-dropzone${fileOrden ? ' auth-dropzone--loaded' : ''}`}
                      onClick={() => document.getElementById('fileInput').click()}
                    >
                      <input
                        id="fileInput"
                        type="file"
                        accept=".pdf,.jpg,.png"
                        style={{ display: 'none' }}
                        onChange={e => setFileOrden(e.target.files[0]?.name || null)}
                      />
                      {fileOrden
                        ? <><span className="auth-dropzone__icon">✅</span><span className="auth-dropzone__text">{fileOrden}</span></>
                        : <><span className="auth-dropzone__icon">📄</span><span className="auth-dropzone__text">Subir orden médica</span><span className="auth-dropzone__hint">PDF, JPG o PNG</span></>
                      }
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── PASO 4: Resumen + Flujograma (aparece al enviar) ──────── */}
            {step === 4 && (
              <div className="auth-step">
                <h3 className="auth-step__title">Revisá y enviá tu solicitud</h3>
                <div className="auth-summary">
                  <div className="auth-summary__row">
                    <span className="auth-summary__key">Tipo</span>
                    <span className="auth-summary__value">{tipoObj?.icon} {tipoObj?.label}</span>
                  </div>
                  <div className="auth-summary__row">
                    <span className="auth-summary__key">Asociado</span>
                    <span className="auth-summary__value">{nroAfiliado}</span>
                  </div>
                  <div className="auth-summary__row">
                    <span className="auth-summary__key">Plan</span>
                    <span className="auth-summary__value">{planObj?.nombre || plan}</span>
                  </div>
                  <div className="auth-summary__row">
                    <span className="auth-summary__key">Práctica</span>
                    <span className="auth-summary__value">
                      {practicaObj?.nombre || '-'}
                      <em className="auth-summary__code"> ({prestacionCodigo})</em>
                    </span>
                  </div>
                  <div className="auth-summary__row">
                    <span className="auth-summary__key">Diagnóstico</span>
                    <span className="auth-summary__value">{diagnosticoObj?.codigo} — {diagnosticoObj?.nombre}</span>
                  </div>
                  <div className="auth-summary__row">
                    <span className="auth-summary__key">Fecha de orden</span>
                    <span className="auth-summary__value">{fechaOrden}</span>
                  </div>
                  {fileOrden && (
                    <div className="auth-summary__row">
                      <span className="auth-summary__key">Orden adjunta</span>
                      <span className="auth-summary__value">📄 {fileOrden}</span>
                    </div>
                  )}
                </div>

                {/* Flujograma: aparece solo cuando se hace click en Enviar */}
                {showSim && (
                  <SimulacionFlujo logs={simLogs} pasoActual={simPaso} activeLogIndex={activeLogIndex} />
                )}
              </div>
            )}

          </div>

          {/* Footer */}
          <div className="auth-card__footer">
            {step === 1
              ? <button className="auth-btn auth-btn--secondary" onClick={handleLimpiar}>Limpiar</button>
              : <button className="auth-btn auth-btn--secondary" onClick={handleBackStep} disabled={loading}>Atrás</button>
            }
            {step < TOTAL_STEPS
              ? (
                <button
                  className="auth-btn auth-btn--primary"
                  onClick={() => setStep(s => s + 1)}
                  disabled={!canNext()}
                >
                  Siguiente
                </button>
              ) : (
                <button
                  className="auth-btn auth-btn--primary"
                  onClick={puedeNuevaSolicitud ? handleLimpiar : handleAutorizar}
                  disabled={(loading && !puedeNuevaSolicitud) || !nroAfiliado}
                >
                  {loading ? (
                    <span className="auth-btn__loading">
                      <span className="auth-btn__spinner" />
                      Evaluando solicitud...
                    </span>
                  ) : (
                    puedeNuevaSolicitud ? 'Generar nueva solicitud' : 'Enviar solicitud'
                  )}
                </button>
              )
            }
          </div>

        </div>
      </div>
    </div>
  );
}

export default Autorizacion;
